// src/utils/otpDigitos.js
//
// O código de verificação mora em N caixas de UM dígito — mas o que entra numa
// caixa nem sempre é um dígito só. O autofill do iOS/Android ("Preencher com
// código de e-mail/SMS") e o Ctrl+V colam os seis de uma vez na caixa focada,
// e um `onChange` que só aceita `^\d?$` joga cinco fora: entrava só o
// primeiro número. Esta função é o único lugar que sabe distribuir.
//
// Puro, sem React: os três formulários de OTP (app, landing/Entrar,
// landing/AssinarFluxo) chamam a mesma regra.

export const OTP_TAMANHO = 6;

/**
 * Distribui o que entrou na caixa `indice` pelas caixas do código.
 *
 * - 1 dígito: ocupa a caixa e o foco vai para a próxima;
 * - código inteiro (autofill/colagem com >= `tamanho` dígitos): sempre
 *   começa da PRIMEIRA caixa, não importa qual estava focada;
 * - colagem parcial: preenche da caixa focada em diante;
 * - vazio (Backspace/limpeza): esvazia a caixa;
 * - caixa já cheia recebendo um segundo caractere: vale o dígito NOVO — o
 *   navegador entrega "12" quando se digita "2" sobre um "1".
 *
 * @param {string[]} atual as caixas como estão
 * @param {number} indice caixa que recebeu a entrada
 * @param {string} bruto o `value` do input (ou o texto colado)
 * @param {number} [tamanho=OTP_TAMANHO]
 * @returns {{ proximo: string[], foco: number }} novas caixas e qual focar
 */
export function distribuirDigitos(atual, indice, bruto, tamanho = OTP_TAMANHO) {
  const base = Array.isArray(atual) ? atual : [];
  const proximo = Array.from({ length: tamanho }, (_, i) => base[i] || "");
  const i0 = Math.min(Math.max(0, Number(indice) || 0), tamanho - 1);

  let digitos = String(bruto ?? "").replace(/\D/g, "");

  if (!digitos) {
    proximo[i0] = "";
    return { proximo, foco: i0 };
  }

  // "1" + tecla "2" chega como "12" (ou "21" se o cursor estava antes).
  if (digitos.length === 2 && proximo[i0]) {
    if (digitos[0] === proximo[i0]) digitos = digitos[1];
    else if (digitos[1] === proximo[i0]) digitos = digitos[0];
  }

  const inicio = digitos.length >= tamanho ? 0 : i0;
  let i = inicio;
  for (const d of digitos) {
    if (i >= tamanho) break;
    proximo[i] = d;
    i += 1;
  }
  return { proximo, foco: Math.min(i, tamanho - 1) };
}
